const RATING_STORAGE_KEY = "ea-content-rating-history";
export const THUMB_VALUES = ["up", "down"];
export const isValidThumbValue = (value) =>
  typeof value === "string" && THUMB_VALUES.includes(value);

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.localStorage !== "undefined";

const MISSING_VERSION_TOKEN = "__ea_rating_missing__";
const TOKEN_PREFIX = Object.freeze({
  millis: "ms:",
  number: "num:",
  string: "str:",
  boolean: "bool:",
  json: "json:",
});

const MILLIS_TOKEN_PATTERN = /^ms:-?\d+$/;
const NUMBER_TOKEN_PATTERN = /^num:-?\d+(?:\.\d+)?$/;
const BOOLEAN_TOKEN_PATTERN = /^bool:(?:0|1)$/;
const STRING_TOKEN_PATTERN = /^str:[\s\S]*$/;
const JSON_TOKEN_PATTERN = /^json:[\s\S]+$/;

const TOKEN_VALIDATORS = Object.freeze({
  [TOKEN_PREFIX.millis]: (value) => MILLIS_TOKEN_PATTERN.test(value),
  [TOKEN_PREFIX.number]: (value) => NUMBER_TOKEN_PATTERN.test(value),
  [TOKEN_PREFIX.boolean]: (value) => BOOLEAN_TOKEN_PATTERN.test(value),
  [TOKEN_PREFIX.string]: (value) => STRING_TOKEN_PATTERN.test(value),
  [TOKEN_PREFIX.json]: (value) => JSON_TOKEN_PATTERN.test(value),
});

const isCanonicalTokenString = (value) => {
  if (typeof value !== "string") return false;
  return Object.entries(TOKEN_VALIDATORS).some(([prefix, validator]) => {
    if (!value.startsWith(prefix)) return false;
    return validator(value);
  });
};

const isNonStringCanonicalToken = (value) => {
  if (typeof value !== "string") return false;
  return [
    TOKEN_PREFIX.millis,
    TOKEN_PREFIX.number,
    TOKEN_PREFIX.boolean,
    TOKEN_PREFIX.json,
  ].some(
    (prefix) => value.startsWith(prefix) && TOKEN_VALIDATORS[prefix](value),
  );
};

const unwrapLegacyToken = (value) => {
  if (typeof value !== "string") return value;
  if (!value.startsWith(TOKEN_PREFIX.string)) return value;

  const original = value;
  let candidate = value;
  let depth = 0;
  const maxDepth = 5;

  while (
    typeof candidate === "string" &&
    candidate.startsWith(TOKEN_PREFIX.string) &&
    depth < maxDepth
  ) {
    const withoutPrefix = candidate.slice(TOKEN_PREFIX.string.length);
    if (isNonStringCanonicalToken(withoutPrefix)) {
      return withoutPrefix;
    }

    candidate = withoutPrefix;
    depth += 1;
  }

  return original;
};

const sanitizeStoredToken = (value) => {
  if (typeof value !== "string") return null;

  const unwrapped = unwrapLegacyToken(value);
  if (isCanonicalTokenString(unwrapped)) {
    return unwrapped;
  }

  if (isCanonicalTokenString(value)) {
    return value;
  }

  return `${TOKEN_PREFIX.string}${value}`;
};

const coerceMillis = (value) => {
  if (!value || typeof value !== "object") return null;
  if (typeof value.toMillis === "function") {
    const millis = value.toMillis();
    return Number.isFinite(millis) ? millis : null;
  }
  if (typeof value.toDate === "function") {
    const date = value.toDate();
    if (date instanceof Date) {
      const millis = date.getTime();
      return Number.isFinite(millis) ? millis : null;
    }
  }
  const seconds =
    typeof value.seconds === "number"
      ? value.seconds
      : typeof value._seconds === "number"
      ? value._seconds
      : null;
  if (seconds === null) return null;
  const nanos =
    typeof value.nanoseconds === "number"
      ? value.nanoseconds
      : typeof value._nanoseconds === "number"
      ? value._nanoseconds
      : 0;
  return seconds * 1000 + Math.floor(nanos / 1e6);
};

const normalizeVersionToken = (value, options = {}) => {
  const fromStore = Boolean(options.fromStore);
  if (value === null || typeof value === "undefined") {
    return MISSING_VERSION_TOKEN;
  }

  if (value === MISSING_VERSION_TOKEN) {
    return MISSING_VERSION_TOKEN;
  }

  if (value instanceof Date) {
    return `${TOKEN_PREFIX.millis}${value.getTime()}`;
  }

  const millis = coerceMillis(value);
  if (millis !== null && typeof millis !== "undefined") {
    return `${TOKEN_PREFIX.millis}${millis}`;
  }

  const valueType = typeof value;
  if (valueType === "number" && Number.isFinite(value)) {
    return `${TOKEN_PREFIX.number}${value}`;
  }
  if (valueType === "bigint") {
    return `${TOKEN_PREFIX.number}${value.toString()}`;
  }
  if (valueType === "string") {
    if (fromStore) {
      return sanitizeStoredToken(value);
    }
    return `${TOKEN_PREFIX.string}${value}`;
  }
  if (valueType === "boolean") {
    return `${TOKEN_PREFIX.boolean}${value ? 1 : 0}`;
  }

  try {
    return `${TOKEN_PREFIX.json}${JSON.stringify(value)}`;
  } catch (error) {
    console.warn("Failed to serialize version token; using fallback.", error);
    return `${TOKEN_PREFIX.string}${String(value)}`;
  }
};

const safeParse = (raw) => {
  if (!raw || typeof raw !== "string") return {};
  try {
    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch (error) {
    console.warn("Failed to parse stored ratings; resetting.", error);
    return {};
  }
};

const cleanupStore = (store) => {
  if (!store || typeof store !== "object") {
    return { cleaned: {}, mutated: Boolean(store) };
  }

  const cleaned = {};
  let mutated = false;

  Object.entries(store).forEach(([type, entries]) => {
    if (typeof type !== "string" || !entries || typeof entries !== "object") {
      mutated = true;
      return;
    }

    Object.entries(entries).forEach(([id, value]) => {
      if (!id || typeof id !== "string") {
        mutated = true;
        return;
      }

      try {
        const normalized = normalizeVersionToken(value, { fromStore: true });
        if (!cleaned[type]) {
          cleaned[type] = {};
        }
        cleaned[type][id] = normalized;
        if (normalized !== value) {
          mutated = true;
        }
      } catch (error) {
        mutated = true;
        console.warn("Failed to normalize rating entry", { type, id, error });
      }
    });

    if (cleaned[type] && Object.keys(cleaned[type]).length === 0) {
      delete cleaned[type];
      mutated = true;
    }
  });

  return { cleaned, mutated };
};

const writeRatingStore = (store) => {
  if (!isBrowser()) return;
  try {
    window.localStorage.setItem(RATING_STORAGE_KEY, JSON.stringify(store));
  } catch (error) {
    console.warn("Failed to persist rating history.", error);
  }
};

export const readRatingStore = () => {
  if (!isBrowser()) return {};
  const raw = window.localStorage.getItem(RATING_STORAGE_KEY);
  const parsed = safeParse(raw);
  const { cleaned, mutated } = cleanupStore(parsed);
  if (mutated) {
    writeRatingStore(cleaned);
  }
  return cleaned;
};

export const hasRecentRating = (type, id, contentVersion) => {
  if (!type || !id) return false;
  const normalizedVersion = normalizeVersionToken(contentVersion);
  const store = readRatingStore();
  const storedVersion = store?.[type]?.[id];
  if (typeof storedVersion !== "string") return false;
  return storedVersion === normalizedVersion;
};

export const recordRating = (type, id, contentVersion) => {
  if (!type || !id || !isBrowser()) return;
  const store = readRatingStore();
  if (!store[type]) {
    store[type] = {};
  }
  store[type][id] = normalizeVersionToken(contentVersion);
  writeRatingStore(store);
};

export const getRatingVersionKey = (contentVersion) =>
  normalizeVersionToken(contentVersion);

const coerceNonNegativeNumber = (value) => {
  const numeric = Number(value);
  if (Number.isFinite(numeric) && numeric > 0) {
    return Math.round(numeric);
  }
  return 0;
};

export const getThumbCounts = (userRating) => {
  const up = coerceNonNegativeNumber(userRating?.up);
  const down = coerceNonNegativeNumber(userRating?.down);
  return { up, down };
};

export const getTotalThumbs = (userRating) => {
  const { up, down } = getThumbCounts(userRating || {});
  return up + down;
};

export const getApprovalRate = (userRating) => {
  const total = getTotalThumbs(userRating);
  if (!total) return null;
  const { up } = getThumbCounts(userRating);
  return up / total;
};

export const formatApprovalLabel = (userRating, digits = 0) => {
  const rate = getApprovalRate(userRating);
  if (rate === null) return null;
  const percent = rate * 100;
  const factor = Math.pow(10, digits);
  const rounded = Math.round(percent * factor) / factor;
  return `${rounded}% positive`;
};

export const getThumbBreakdownArray = (userRating) => {
  const { up, down } = getThumbCounts(userRating || {});
  return [
    { key: "up", label: "Thumbs up", icon: "mdi-thumb-up-outline", count: up },
    {
      key: "down",
      label: "Thumbs down",
      icon: "mdi-thumb-down-outline",
      count: down,
    },
  ];
};

export const hasAnyRatings = (userRating) => getTotalThumbs(userRating) > 0;

export {
  formatRatingFeedbackCountLabel,
  getRatingFeedbackCounts,
} from "./contentRatingFeedback.mjs";
